import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Icon, Input, Label, toast } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { queryKeys } from "../../../../lib/query-keys";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceContext, useWorkspaceDetails, useWorkspaceId } from "../../../../session/workspace-context";
import { useFormatters } from "../../shared";
import { deleteConfirmed } from "../settings-model";
import { Gate, useCanManage } from "./panel-kit";

function DeleteDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const format = useFormatters();
  const { summary } = useWorkspaceContext();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!deleteConfirmed(typed, summary.name)) return;
    setBusy(true);
    try {
      const result = await unwrap(api.DELETE("/v1/workspace", { body: { confirm_name: typed.trim() } }));
      toast.warning(t("settings.privacy.deleteScheduled", { workspace: summary.name, date: format.date(result.deletion_scheduled_for) }));
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace(wsId) });
      onClose();
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent title={t("settings.privacy.deleteTitle", { workspace: summary.name })} description={t("settings.privacy.deleteBody")} closeLabel={t("common.close")} className="max-w-md">
        <form className="space-y-4" onSubmit={(event) => void submit(event)}>
          <div>
            <Label htmlFor="delete-confirm">{t("settings.privacy.deleteConfirm", { workspace: summary.name })}</Label>
            <Input id="delete-confirm" autoComplete="off" value={typed} onChange={(event) => { setTyped(event.target.value); }} />
          </div>
          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="danger" loading={busy} disabled={!deleteConfirmed(typed, summary.name)}>
              {t("settings.privacy.deleteAction")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Danger zone: schedule deletion (14-day grace, typed confirm) or cancel a scheduled one. */
export function DangerZone() {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const format = useFormatters();
  const details = useWorkspaceDetails();
  const manage = useCanManage("workspace.delete");
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const scheduled = details.data?.deletion_scheduled_for ?? null;
  const cancel = async () => {
    setCancelling(true);
    try {
      await unwrap(api.POST("/v1/workspace/deletion/cancel"));
      toast.success(t("settings.privacy.deleteCancelled"));
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace(wsId) });
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setCancelling(false);
    }
  };
  return (
    <section className="card border-danger" aria-labelledby="danger-title">
      <div className="card-header">
        <h2 id="danger-title" className="card-title text-danger">
          {t("settings.privacy.dangerTitle")}
        </h2>
      </div>
      <div className="card-body flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        {scheduled ? (
          <div role="status">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Icon name="alert-triangle" className="size-4 text-danger" />
              {t("settings.privacy.deletePending", { date: format.date(scheduled) })}
            </div>
            <p className="text-xs text-fg-muted">{t("settings.privacy.deletePendingBody")}</p>
          </div>
        ) : (
          <div>
            <div className="text-sm font-semibold">{t("settings.privacy.deleteHeading")}</div>
            <p className="text-xs text-fg-muted">{t("settings.privacy.deleteSummary")}</p>
          </div>
        )}
        <Gate reason={manage.reason}>
          {scheduled ? (
            <Button variant="secondary" className="shrink-0" disabled={!manage.allowed} loading={cancelling} onClick={() => void cancel()}>
              {t("settings.privacy.deleteCancel")}
            </Button>
          ) : (
            <Button variant="danger" className="shrink-0" disabled={!manage.allowed} onClick={() => { setOpen(true); }}>
              <Icon name="trash" />
              {t("settings.privacy.deleteAction")}
            </Button>
          )}
        </Gate>
      </div>
      {open ? <DeleteDialog onClose={() => { setOpen(false); }} /> : null}
    </section>
  );
}
